/**
 * Sequence Builder Component
 *
 * Visual builder for creating multi-step WhatsApp template sequences
 */

import React, { useState, useEffect } from 'react';
import { Box, Paper, Grid, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, IconButton, Card, CardContent, Alert, Divider, Chip, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowDownward as ArrowDownIcon, ArrowUpward as ArrowUpIcon, Save as SaveIcon } from '@mui/icons-material';
import { TemplateSequence, SequenceStep, SequenceCreateRequest, WhatsAppTemplate } from '../../types/whatsapp-templates';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';
import { SelectChangeEvent } from '@mui/material';

type TriggerType = 'manual' | 'session_closed' | 'no_response';

interface SequenceBuilderProps {
  accountId: number;
  initialSequence?: TemplateSequence;
  onSave: (sequenceData: SequenceCreateRequest) => Promise<void>;
  onCancel: () => void;
}

const SequenceBuilder: React.FC<SequenceBuilderProps> = ({
  accountId,
  initialSequence,
  onSave,
  onCancel
}) => {
  // Form state
  const [name, setName] = useState(initialSequence?.name || '');
  const [description, setDescription] = useState(initialSequence?.description || '');
  const [triggerType, setTriggerType] = useState(initialSequence?.trigger_type || 'manual');
  const [isActive, setIsActive] = useState(initialSequence?.is_active ?? true);
  const [steps, setSteps] = useState<SequenceStep[]>(
    initialSequence?.sequence_steps || []
  );

  // Available templates
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Load approved templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await whatsappTemplatesApi.listTemplates(accountId, {
          status: 'APPROVED'
        });

        if (response.success) {
          setTemplates(response.templates);
        }
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [accountId]);

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name || name.length === 0) {
      newErrors.name = 'Sequence name is required';
    }

    if (steps.length === 0) {
      newErrors.steps = 'At least one step is required';
    }

    steps.forEach((step, index) => {
      if (!step.template_id) {
        newErrors[`step_${index}_template`] = 'Template is required';
      }

      if (step.delay_hours < 0) {
        newErrors[`step_${index}_delay`] = 'Delay cannot be negative';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleAddStep = () => {
    const newStep: SequenceStep = {
      step: steps.length + 1,
      template_id: 0,
      delay_hours: 0,
      conditions: {
        send_only_if_no_response: true,
        stop_if_session_reopened: true
      }
    };

    setSteps([...steps, newStep]);
  };

  const handleRemoveStep = (index: number) => {
    const updated = steps.filter((_, i) => i !== index);
    // Renumber steps
    const renumbered = updated.map((step, i) => ({ ...step, step: i + 1 }));
    setSteps(renumbered);
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= steps.length) return;

    const updated = [...steps];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    // Renumber steps
    const renumbered = updated.map((step, i) => ({ ...step, step: i + 1 }));
    setSteps(renumbered);
  };

  const handleStepChange = (index: number, field: keyof SequenceStep, value: any) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    setSteps(updated);
  };

  const handleConditionChange = (index: number, condition: string, value: boolean) => {
    const updated = [...steps];
    updated[index] = {
      ...updated[index],
      conditions: {
        ...updated[index].conditions,
        [condition]: value
      }
    };
    setSteps(updated);
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      const sequenceData: SequenceCreateRequest = {
        name,
        description: description || undefined,
        sequence_steps: steps,
        trigger_type: triggerType,
        is_active: isActive
      };

      await onSave(sequenceData);
    } catch (error) {
      console.error('Error saving sequence:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Left Panel - Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sequence Configuration
            </Typography>

            {/* Basic Info */}
            <Box display="flex" flexDirection="column" gap={3} mt={2}>
              <TextField
                fullWidth
                label="Sequence Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name || 'Give your sequence a descriptive name'}
                placeholder="Abandoned Cart Recovery"
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Sequence to re-engage customers who abandoned their cart"
              />

              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={triggerType}
                  onChange={(e: SelectChangeEvent) => setTriggerType(e.target.value as TriggerType)}
                  label="Trigger Type"
                >
                  <MenuItem value="manual">Manual (Start via API)</MenuItem>
                  <MenuItem value="session_closed">Session Closed</MenuItem>
                  <MenuItem value="no_response">No Response After Template</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />

              <Divider />

              {/* Sequence Steps */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Sequence Steps
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddStep}
                    variant="outlined"
                    size="small"
                  >
                    Add Step
                  </Button>
                </Box>

                {errors.steps && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.steps}
                  </Alert>
                )}

                {loadingTemplates ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : templates.length === 0 ? (
                  <Alert severity="warning">
                    No approved templates available. Create and approve templates first.
                  </Alert>
                ) : (
                  <Box display="flex" flexDirection="column" gap={2}>
                    {steps.map((step, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Box display="flex" gap={2} alignItems="flex-start">
                            {/* Step Number */}
                            <Box
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                flexShrink: 0
                              }}
                            >
                              {step.step}
                            </Box>

                            {/* Step Configuration */}
                            <Box flex={1}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!errors[`step_${index}_template`]}
                                  >
                                    <InputLabel>Template</InputLabel>
                                    <Select
                                      value={step.template_id || ''}
                                      onChange={(e) =>
                                        handleStepChange(index, 'template_id', Number(e.target.value))
                                      }
                                      label="Template"
                                    >
                                      {templates.map((template) => (
                                        <MenuItem key={template.id} value={template.id}>
                                          {template.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Delay (hours)"
                                    value={step.delay_hours}
                                    onChange={(e) =>
                                      handleStepChange(index, 'delay_hours', Number(e.target.value))
                                    }
                                    error={!!errors[`step_${index}_delay`]}
                                    helperText={
                                      index === 0
                                        ? 'First step sends immediately'
                                        : `Wait ${step.delay_hours}h after previous step`
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <Box display="flex" flexDirection="column" gap={1}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={step.conditions?.send_only_if_no_response ?? true}
                                          onChange={(e) =>
                                            handleConditionChange(
                                              index,
                                              'send_only_if_no_response',
                                              e.target.checked
                                            )
                                          }
                                          size="small"
                                        />
                                      }
                                      label={
                                        <Typography variant="caption">
                                          Only send if user hasn't responded
                                        </Typography>
                                      }
                                    />

                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={step.conditions?.stop_if_session_reopened ?? true}
                                          onChange={(e) =>
                                            handleConditionChange(
                                              index,
                                              'stop_if_session_reopened',
                                              e.target.checked
                                            )
                                          }
                                          size="small"
                                        />
                                      }
                                      label={
                                        <Typography variant="caption">
                                          Stop if session reopens
                                        </Typography>
                                      }
                                    />
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Actions */}
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveStep(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => handleMoveStep(index, 'down')}
                                disabled={index === steps.length - 1}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveStep(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}

                    {steps.length === 0 && (
                      <Alert severity="info">
                        No steps added. Click "Add Step" to create your sequence.
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || loadingTemplates}
                >
                  {saving ? 'Saving...' : initialSequence ? 'Update Sequence' : 'Create Sequence'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Preview */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Sequence Overview
            </Typography>

            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Name:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {name || 'Untitled Sequence'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Trigger:
                </Typography>
                <Chip label={triggerType} size="small" sx={{ mt: 0.5 }} />
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Total Steps:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {steps.length}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Total Duration:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {steps.reduce((sum, step) => sum + step.delay_hours, 0)} hours
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Status:
                </Typography>
                <Chip
                  label={isActive ? 'Active' : 'Inactive'}
                  color={isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              {steps.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Timeline:
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                      {steps.map((step, index) => {
                        const template = templates.find((t) => t.id === step.template_id);
                        const cumulativeTime = steps
                          .slice(0, index + 1)
                          .reduce((sum, s) => sum + s.delay_hours, 0);

                        return (
                          <Box key={index}>
                            <Typography variant="caption" fontWeight="medium">
                              Step {step.step} ({cumulativeTime}h)
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {template?.name || 'Select template'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SequenceBuilder;
